<template>
  <el-form
    label-position="top"
    label-width="100px"
    style="padding: 0;"
    class="course-edit-form__container"
    :model="form"
    :rules="validationRules"
    @validate="onValidateChange"
  >
    <div class="course-edit-form__contents-container">
      <div class="course-edit-form__contents-wrapper">
        <el-form-item
          class="course-edit-form__name"
          label="수업명"
          prop="name"
        >
          <el-input
            v-model="form.name"
            :max="100"
            placeholder="수업명을 입력해주세요."
          />
        </el-form-item>
        <section class="course-edit-form__single-forms">
          <article class="course-edit-form__single-form">
            <el-form-item label="카테고리 (미선택시 기본 카테고리로 배정)">
              <el-select
                v-model="form.category"
                placeholder="카테고리를 선택해주세요."
                style="width: 100%;"
              >
                <el-option
                  v-for="category in categories"
                  :key="category.id"
                  :label="category.name"
                  :value="category.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item
              label="담당 강사"
              prop="instructors"
            >
              <el-select
                v-model="form.instructors"
                multiple
                collapse-tags
                placeholder="본 수업에 배정된 강사를 모두 선택해주세요."
              >
                <el-option
                  v-for="item in instructorOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                  :disabled="decideItemDisabled(item)"
                >
                  <section :class="item.is_removed? 'option-wrapper__removed' : 'option-wrapper__used'">
                    <span class="option-label">
                      {{ item.label }}
                    </span>
                    <span class="option-removed">
                      {{ item.is_removed ? '삭제됨' : '' }}
                    </span>
                  </section>
                </el-option>
              </el-select>
            </el-form-item>
            <section class="course-edit-form__single-form__number-selectors">
              <el-form-item 
                label="수업 시간"
                class="course-edit-form__single-form__number-selector"
              >
                <div class="el-input-group__unit">
                  <el-input-number
                    v-model="form.duration"
                    controls-position="right"
                    :min="1"
                  />
                  <div class="input-unit">
                    분
                  </div>
                </div>
              </el-form-item>
              <el-form-item
                :label="presentQuotaLabel"
                class="course-edit-form__single-form__number-selector"
              >
                <div class="el-input-group__unit">
                  <el-input-number
                    v-model="form.quota"
                    controls-position="right"
                    :min="1"
                  />
                  <div class="input-unit">
                    명
                  </div>
                </div>
              </el-form-item>
            </section>
          </article>
          <article class="course-edit-form__single-form">
            <el-form-item>
              <div slot="label">
                수업 유형
                <popover>
                  온라인/비대면으로 진행되는 수업일 경우<br>
                  ‘온라인’을 선택해 주세요. 스케줄 등록 시 입력한<br>
                  각 링크로 회원들을 바로 참가시킬 수 있습니다.
                </popover>
              </div>
              <el-checkbox
                border
                true-label="OFFLINE"
                false-label="OFFLINE"
                v-model="checker.is_offline"
                @change="checkboxActionHandler"
              >
                오프라인 
              </el-checkbox>
              <el-checkbox
                border
                true-label="ONLINE"
                false-label="ONLINE"
                v-model="checker.is_online"
                @change="checkboxActionHandler"
              >
                온라인
              </el-checkbox>
            </el-form-item>
            <section class="course-edit-form__single-form__course-infos">
              <el-form-item :label="membershipUnit.label">
                <div class="el-input-group__unit">
                  <el-input-comma-number
                    v-model="form.price"
                    controls-position="right"
                    :min="1"
                  />
                  <div class="input-unit">
                    {{ membershipUnit.unit }}
                  </div>
                </div>
              </el-form-item>
              <el-form-item
                label="색상"
                prop="color"
              >
                <el-color-picker v-model="form.color" />
              </el-form-item>
            </section>
            <el-form-item>
              <div slot="label">
                수강 제한 여부
                <popover>
                  모든 회원이 수강할 수 있는 수업은<br>
                  ‘누구나 수강 가능’을, 특정 회원/그룹만 수강할 수<br>
                  있는 수업은 ‘수강 제한’을 선택해 주세요.
                </popover>
              </div>
              <el-checkbox
                border
                true-label="PUBLIC"
                false-label="PUBLIC"
                v-model="checker.is_public"
                @change="checkboxActionHandler"
              >
                누구나 수강 가능 
              </el-checkbox>
              <el-checkbox
                border
                true-label="PRIVATE"
                false-label="PRIVATE"
                v-model="checker.is_private"
                @change="checkboxActionHandler"
              >
                수강 제한
              </el-checkbox>
            </el-form-item>
          </article>
        </section>
      </div>
    </div>
    <div class="d-md-flex justify-content-between mt-3 no-gutters  mb-3">
      <div class="col" />
      <div
        v-show="form.id"
        class="col-12 col-md-6 px-5 px-md-0 form-bottom-description"
      >
        <strong>수업 명, 카테고리, 수강 제한 여부, 색상</strong>‌을 수정할 경우 현재까지 등록된 모든 수업에 바로 적용되며,<br><strong>이 외 나머지 항목</strong> 수정 시에는 앞으로 새로 등록될 수업‌의 기본 정보로 적용됩니다.
      </div>
      <div class="col d-flex justify-content-end align-items-end">
        <div>
          <el-button
            class="light-gray"
            @click="handleClickCancelForm"
          >
            취소
          </el-button>
          <el-loading-button
            class="black"
            :disabled="!isFormValidated"
            :is-loading="isSavingForm"
            @click="handleClickSaveForm"
          >
            저장
          </el-loading-button>
        </div>
      </div>
    </div>
  </el-form>
</template>

<script>
import { fiflboxApi } from '@/utils/api';
import {
  sortRemoveForSelectBox,
  makeInstructorsForSelectBox
} from '@/utils/array-helper';

import Popover from '@/components/popover';
import ElLoadingButton from '@/components/el-loading-button';
import ElInputCommaNumber from '@/components/element-ui/ElInputCommaNumber';

export default {
  name: 'CourseEditForm',
  components: { ElLoadingButton, Popover, ElInputCommaNumber },
  props: ['data', 'membershipSystem'],
  data() {
    return {
      form: {
        id: null,
        instructors: [],
        name: '',
        price: 1,
        color: '#000000',
        duration: 60,
        quota: 10,
        category: null,
        is_offline: true,
        is_public: true
      },
      validationRules: {
        name: [{ required: true, message: ' ', trigger: 'blur' }]
      },
      isFormValid: {
        name: false,
        instructors: false
      },
      instructors: [],
      categories: [],
      isSavingForm: false,
      checker: {
        is_offline: undefined,
        is_online: undefined,
        is_public: undefined,
        is_private: undefined
      },
      initDataForSelectBox: {
        instructors: []
      }
    };
  },
  computed: {
    isFormValidated() {
      let formName = this.form.name && this.form.name !== '';

      return formName;
    },
    membershipUnit() {
      if (this.membershipSystem === 'POINT') {
        return {
          label: '차감 포인트',
          unit: 'P'
        };
      }

      return {
        label: '차감 횟수',
        unit: '회'
      };
    },
    isBookingSystem() {
      return this.$store.getters.getIsBookingSystem;
    },
    presentQuotaLabel() {
      return this.isBookingSystem ? '예약 정원' : '수업 정원';
    },
    instructorOptions() {
      return sortRemoveForSelectBox(
        makeInstructorsForSelectBox(
          this.instructors,
          this.initDataForSelectBox.instructors
        )
      );
    }
  },
  async created() {
    if (this.data) {
      this.form = Object.assign({}, this.data);
    }

    this.initDataForSelectBox.instructors = this.form.instructors;

    this.getCheckersStatus();
    await this.getCategories();
    await this.getInstructors();
  },
  watch: {
    isSavingForm: function(value) {
      if (value) {
        this.form.is_public = this.checker.is_public;
        this.form.is_offline = this.checker.is_offline;
      }
    }
  },
  methods: {
    onValidateChange($event, isValidated) {
      this.isFormValid[$event] = isValidated;
    },
    async getCategories() {
      let { data } = await fiflboxApi.v2.getCourseCategories({
        is_removed: false
      });
      this.categories = data;
    },
    async getInstructors() {
      let { data } = await fiflboxApi.v2.getCenterInstructors();
      this.instructors = data;
    },
    async getCheckersStatus() {
      // get form value
      const {
        is_public: form_is_public,
        is_offline: form_is_offline
      } = this.form;
      // get is_public value, set checker initial value
      const {
        is_public = form_is_public ? true : false,
        is_private = !form_is_public ? true : false
      } = this.checker;
      // get is_offline value, set checker initial value
      const {
        is_offline = form_is_offline ? true : false,
        is_online = !form_is_offline ? true : false
      } = this.checker;
      // update checker
      this.checker = { is_public, is_private, is_offline, is_online };
    },
    async handleClickSaveForm() {
      this.isSavingForm = true;
      if (!this.form.category) {
        this.form.category = this.categories[0].id;
      }

      try {
        if (this.form.id) {
          let parentCourseId = this.form.id;
          await fiflboxApi.v1.putParentCourses(parentCourseId, this.form);
          this.$message('수업이 수정됐습니다.');
        } else {
          await fiflboxApi.v1.postParentCourses(this.form);
          this.$message('수업이 등록됐습니다.');
        }
        return this.$emit('save', this.form);
      } catch (err) {
      } finally {
        this.isSavingForm = false;
      }
    },
    async handleClickCancelForm() {
      this.$emit('cancel');
    },
    decideItemDisabled(item) {
      if (this.form.instructors.includes(item.id)) {
        return false;
      }

      return item.is_removed;
    },
    // 체크박스를 라디오 버튼처럼 동작시키기 위한 핸들러
    checkboxActionHandler(action) {
      switch (action) {
        case 'ONLINE':
          this.checker.is_online = true;
          this.checker.is_offline = false;
          break;
        case 'OFFLINE':
          this.checker.is_offline = true;
          this.checker.is_online = false;
          break;
        case 'PUBLIC':
          this.checker.is_public = true;
          this.checker.is_private = false;
          break;
        case 'PRIVATE':
          this.checker.is_private = true;
          this.checker.is_public = false;
          break;
      }
    }
  }
};
</script>
<style lang="scss">
.course-edit-form__container {
  .course-edit-form__contents-container {
    box-sizing: border-box;
    margin: 0 !important;
    padding: 40px 97px;
    background-color: #ffffff;
    border-radius: 6px;
    box-shadow: 0 2px 4px 0 rgba(180, 180, 180, 0.5);
    @media screen and (max-width: 1024px) {
      padding: 40px 20px;
    }

    @media screen and (max-width: 768px) {
      padding: 20px;
    }
    .course-edit-form__contents-wrapper {
      max-width: 768px;
      margin: auto;
      .course-edit-form__name {
        width: 100%;
      }
      .course-edit-form__single-forms {
        display: flex;
        @media screen and (max-width: 768px) {
          flex-wrap: wrap;
        }
        .course-edit-form__single-form {
          box-sizing: border-box;
          width: 100%;
          &:last-child {
            margin-left: 30px;
            @media screen and (max-width: 768px) {
              margin-left: 0;
              margin-top: 20px;
            }
          }
          .el-checkbox {
            margin: 0;
            height: 50px;
            border-radius: 6px;
            padding: 10px;
            display: flex;

            &:last-child {
              margin-left: 10px;
            }
            .el-checkbox__input {
              width: 14px;
              height: 14px;
              margin: auto;
            }
            .el-checkbox__label {
              margin: auto;
              line-height: 20px;
              font-size: 14px;
              font-weight: normal;
            }
          }

          .course-edit-form__single-form__number-selectors {
            display: flex;
            justify-content: space-between;
            .course-edit-form__single-form__number-selector {
              margin: 0;
            }
          }

          .el-form-item:last-child {
            margin-bottom: 0;
          }

          .el-form-item__content {
            display: flex;
          }

          .course-edit-form__single-form__course-infos {
            display: flex;
            .el-form-item:first-child {
              margin-right: 50px;
            }
          }
        }
      }
    }
  }
}

.form-bottom-description {
  font-size: 14px;
  line-height: 22px;
  color: #9b9b9b;
  @media screen and (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
}
</style>
